import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, ChevronRight, Sparkles, Store, ShieldCheck, Tag } from "lucide-react";
import localApi from "@/lib/localApi";
import { useT } from "@/lib/i18n";
import MenuButton from "@/components/mobile/MenuButton";

const ICON_BY_KIND = {
  new_offer: Tag,
  new_shop: ShieldCheck,
  shop_status: Store,
  default: Sparkles,
};

function timeAgo(iso, lang) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const s = Math.floor(diff / 1000);
    if (s < 60) return lang === "ml" ? "ഇപ്പോൾ" : "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d`;
    return new Date(iso).toLocaleDateString(lang === "ml" ? "ml-IN" : "en-GB");
  } catch {
    return "";
  }
}

export default function MobileNotifications() {
  const { t, lang } = useT();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  const load = () => {
    setLoading(true);
    localApi
      .get("/notifications")
      .then(({ data }) => setItems(data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openOne = async (n) => {
    // Mark read then navigate to deep link
    try {
      if (!n.is_read) {
        await localApi.post(`/notifications/${n.id}/read`);
        setItems((list) => list.map((it) => (it.id === n.id ? { ...it, is_read: true } : it)));
        window.dispatchEvent(new CustomEvent("nd_notifications_changed"));
      }
    } catch { /* noop */ }
    if (n.deep_link) nav(n.deep_link);
  };

  const markAllRead = async () => {
    setMarking(true);
    try {
      await localApi.post("/notifications/read-all");
      setItems((list) => list.map((it) => ({ ...it, is_read: true })));
      window.dispatchEvent(new CustomEvent("nd_notifications_changed"));
    } catch { /* noop */ }
    finally { setMarking(false); }
  };

  const unread = items.filter((i) => !i.is_read).length;

  return (
    <div className="px-5 pt-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">{t("notif.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("notif.subtitle")}</p>
        </div>
        <MenuButton />
      </div>

      <div className="flex items-center justify-between mt-5 mb-3">
        <span className="text-sm font-semibold text-muted-foreground">
          {unread > 0 ? t("notif.unreadCount", unread) : t("notif.allCaughtUp")}
        </span>
        {unread > 0 && (
          <button
            data-testid="notif-mark-all-read"
            onClick={markAllRead}
            disabled={marking}
            className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:opacity-80 disabled:opacity-50 transition-opacity"
          >
            <CheckCheck className="w-4 h-4" /> {t("notif.markAllRead")}
          </button>
        )}
      </div>

      {loading ? (
        <ul className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <li key={i} className="h-20 rounded-2xl bg-secondary animate-pulse" />
          ))}
        </ul>
      ) : items.length === 0 ? (
        <div className="grid place-items-center py-24 text-center">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-secondary text-primary mb-4">
            <Bell className="w-7 h-7" />
          </span>
          <p className="font-display font-bold text-xl">{t("notif.none")}</p>
          <p className="text-muted-foreground text-sm mt-1">{t("notif.noneHint")}</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="notif-list">
          {items.map((n) => {
            const Icon = ICON_BY_KIND[n.kind] || ICON_BY_KIND.default;
            return (
              <li key={n.id}>
                <button
                  data-testid={`notif-${n.id}`}
                  onClick={() => openOne(n)}
                  className={`w-full text-left flex items-start gap-3 rounded-2xl px-3.5 py-3 border transition-colors ${
                    n.is_read
                      ? "bg-card border-border"
                      : "bg-primary/5 border-primary/25 hover:bg-primary/10"
                  }`}
                >
                  <span
                    className={`grid place-items-center w-11 h-11 rounded-full shrink-0 ${
                      n.is_read ? "bg-secondary text-primary" : "bg-primary text-primary-foreground"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`truncate ${n.is_read ? "font-semibold" : "font-extrabold"}`}>{n.title}</p>
                      <span className="text-xs text-muted-foreground shrink-0">{timeAgo(n.created_at, lang)}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                  </div>
                  {!n.is_read && (
                    <span className="mt-1 w-2.5 h-2.5 rounded-full bg-primary shrink-0" />
                  )}
                  <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0 mt-2 hidden sm:block" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
